"use client";

import { useContext, useEffect, useRef, useState } from "react";

import { EndpointType } from "../../../constants/Endpoints";
import { useDevice } from "../../../hooks/useDevice";
import {
  ValidatorFileContext,
  ValidatorModelsContext,
} from "../../../hooks/useFile";
import { fileUpload } from "../../../hooks/useImageUploader";
import { InferenceConfig } from "../../../types/demo";
import { FileUploadCallbacks, FileUploadConfig } from "../../../types/upload";
import {
  base64StringToFile,
  getResultFile,
  isSampleFile,
} from "../../../utils/file";
import { endpointStore } from "../../../utils/mobx/EndpointStore";
import { getAccessibleModels } from "../../../utils/modelFilter";

type UseConfigSidebarOptions<T extends InferenceConfig> = {
  endpointType: EndpointType;
  onClose: () => void;
  getCurrentConfig: () => T;
  defaultConfig: T;
  resetStoreConfig: () => void;
  getResetConfig: () => T;
};

export function useConfigSidebar<T extends InferenceConfig>({
  endpointType,
  onClose,
  getCurrentConfig,
  defaultConfig,
  resetStoreConfig,
  getResetConfig,
}: UseConfigSidebarOptions<T>) {
  const [config, setConfig] = useState<T>(getCurrentConfig());
  const [isOverflowing, setIsOverflowing] = useState(false);

  const contentRef = useRef<HTMLDivElement>(null);

  const { isMobile } = useDevice();
  const { userSettings } = endpointStore;

  const { models, selectedModelIndex } = useContext(ValidatorModelsContext);
  const {
    files,
    selectedFileIndex,
    inferenceState,
    setSelectedFileIndex,
    setFiles,
    setInferenceState,
  } = useContext(ValidatorFileContext);

  const accessibleModels = getAccessibleModels(
    userSettings?.model_access_role || "",
    endpointType,
  );

  // 각 파일에 적용된 설정 가져오기
  useEffect(() => {
    const file = getResultFile(files, selectedFileIndex);
    if (!file) return;

    setConfig((file.inferenceConfig ?? defaultConfig) as T);
  }, [selectedFileIndex, files]);

  // 설정 사이드바 오버플로우 감지. 오버플로우 되면 하단에 border 구분선 활성화
  useEffect(() => {
    const checkOverflow = () => {
      if (contentRef.current) {
        const isOverflowing =
          contentRef.current.scrollHeight > contentRef.current.clientHeight;
        setIsOverflowing(isOverflowing);
      }
    };

    const resizeObserver = new ResizeObserver(checkOverflow);

    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    checkOverflow();
    window.addEventListener("resize", checkOverflow);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", checkOverflow);
    };
  }, [config]);

  // 기본 설정으로 리셋
  const handleReset = () => {
    resetStoreConfig();
    setConfig(getResetConfig());
  };

  // 설정 적용
  const handleApply = async () => {
    if (isMobile) {
      onClose();
    }

    const resultFile = getResultFile(files, selectedFileIndex);
    if (!resultFile?.result) {
      console.error(
        "Cannot apply config: ResultFile or its result is missing.",
      );
      return;
    }

    const fileUploadCallback: FileUploadCallbacks = {
      setSelectedFileIndex,
      setFiles,
      setInferenceState,
    };

    let inputFile: File;
    let fileUploadConfig: FileUploadConfig;

    // 샘플 파일의 경우 새로운 파일 생성하여 inference 진행
    if (isSampleFile(resultFile)) {
      try {
        inputFile = base64StringToFile(
          resultFile.result.imageBase64[0],
          resultFile.title.split(" ").join("_") + ".png",
          "image/png",
        );
      } catch (error) {
        console.error("Failed to create file from base64 for sample:", error);
        return;
      }

      fileUploadConfig = {
        inputFiles: [inputFile],
        models,
        selectedModelIndex,
        files,
        errorType: 1,
        skipValidation: true,
        inferenceConfig: config,
      };
    } else {
      // --- 일반 파일 처리 ---
      inputFile = resultFile.originFile as File;
      if (!inputFile) {
        console.error("Failed to get origin file");
        return;
      }
      fileUploadConfig = {
        inputFiles: [inputFile],
        models,
        selectedModelIndex,
        files,
        errorType: 1,
        skipValidation: true,
        inferenceConfig: config,
        resultFileIdToUpdate: resultFile.id,
      };
    }

    await fileUpload(fileUploadConfig, fileUploadCallback);
  };

  return {
    config,
    setConfig,
    isOverflowing,
    contentRef,
    isMobile,
    inferenceState,
    accessibleModels,
    handleReset,
    handleApply,
  };
}
